
import React from "react";
import ReactDOM from "react-dom/client";
/* BEGIN USAGE */
// tweaks-panel.jsx
// Reusable Tweaks shell + form-control helpers.
// Exports (to window): useTweaks, TweaksPanel, TweakSection, TweakRow, TweakSlider,
//   TweakToggle, TweakRadio, TweakSelect, TweakText, TweakNumber, TweakColor, TweakButton.
//
// Owns the host protocol (listens for miaoda:tweaks:activate / miaoda:tweaks:deactivate,
// posts miaoda:tweaks:available / miaoda:tweaks:set-keys / miaoda:tweaks:dismissed) so
// individual prototypes don't re-roll it. Ships a consistent set of controls so you
// don't hand-draw <input type="range">, segmented radios, steppers, etc.
//
// Usage (in an HTML file that loads React + Babel):
//
//   const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
//     "primaryColor": "#D97757",
//     "palette": ["#D97757", "#29261b", "#f6f4ef"],
//     "fontSize": 16,
//     "density": "regular",
//     "dark": false
//   }/*EDITMODE-END*/;
//
//   TWEAK_DEFAULTS must live inline in the HTML file — in a <script type="text/babel"> block,
//   not in a separate .jsx/.js loaded via <script src>. That in-HTML block is the region the
//   host rewrites when the user adjusts a tweak, so keep it wrapped in the /*EDITMODE-BEGIN*/ …
//   /*EDITMODE-END*/ markers and the object between them valid JSON — double-quoted keys, no
//   trailing commas, no comments or expressions — even after you rename the keys. Move it out
//   of the HTML, strip the markers, or use a non-JSON body and tweak edits silently stop persisting.
//
//   function App() {
//     const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
//     return (
//       <div style={{ fontSize: t.fontSize, color: t.primaryColor }}>
//         Hello
//         <TweaksPanel>
//           <TweakSection label="Typography" />
//           <TweakSlider label="Font size" value={t.fontSize} min={10} max={32} unit="px"
//                        onChange={(v) => setTweak('fontSize', v)} />
//           <TweakRadio  label="Density" value={t.density}
//                        options={['compact', 'regular', 'comfy']}
//                        onChange={(v) => setTweak('density', v)} />
//           <TweakSection label="Theme" />
//           <TweakColor  label="Primary" value={t.primaryColor}
//                        options={['#D97757', '#2A6FDB', '#1F8A5B', '#7A5AE0']}
//                        onChange={(v) => setTweak('primaryColor', v)} />
//           <TweakColor  label="Palette" value={t.palette}
//                        options={[['#D97757', '#29261b', '#f6f4ef'],
//                                  ['#475569', '#0f172a', '#f1f5f9']]}
//                        onChange={(v) => setTweak('palette', v)} />
//           <TweakToggle label="Dark mode" value={t.dark}
//                        onChange={(v) => setTweak('dark', v)} />
//         </TweaksPanel>
//       </div>
//     );
//   }
//
// TweakRadio is the segmented control for 2–3 short options (auto-falls-back to
// TweakSelect past ~16/~10 chars per label); reach for TweakSelect directly when
// options are many or long. For color tweaks always curate 3-4 options rather than
// a free picker; an option can also be a whole 2–5 color palette (the stored value
// is the array). The Tweak* controls are a floor, not a ceiling — build custom
// controls inside the panel if a tweak calls for UI they don't cover.
/* END USAGE */
// ─────────────────────────────────────────────────────────────────────────────

// Palette follows the Arco/Semi neutral token system the 创意设计模式 "设计风格"
// panel is designed in (Figma node 2008-22724): a solid white card on neutral
// grays instead of the previous warm-glass look.
//   text-1 #1F2329  text-2 #646A73  text-3 #86909C
//   border #DEE0E3  fill #EBECEE / #F2F3F5  accent(selected/on) #1F2329
const __TWEAKS_STYLE = `
  .twk-panel{position:fixed;right:16px;top:16px;z-index:2147483646;width:280px;
    max-height:calc(100vh - 32px);display:flex;flex-direction:column;
    transform:scale(var(--dc-inv-zoom,1));transform-origin:top right;
    background:#fff;color:#1F2329;
    border:.5px solid #DEE0E3;border-radius:12px;
    box-shadow:0 8px 24px 8px rgba(0,0,0,.04),0 6px 12px rgba(0,0,0,.04),0 2px 6px rgba(0,0,0,.04);
    font:12px/1.5 ui-sans-serif,system-ui,-apple-system,sans-serif;overflow:hidden}
  .twk-hd{display:flex;align-items:center;justify-content:space-between;
    padding:12px 12px 12px 16px;cursor:move;user-select:none;
    border-bottom:.5px solid #EBECEE}
  .twk-hd b{font-size:14px;font-weight:600;letter-spacing:.01em;color:#1F2329}
  .twk-x{appearance:none;border:0;background:transparent;color:#86909C;
    width:24px;height:24px;border-radius:6px;cursor:default;font-size:13px;line-height:1}
  .twk-x:hover{background:#F2F3F5;color:#1F2329}
  .twk-body{padding:14px 16px 16px;display:flex;flex-direction:column;gap:14px;
    overflow-y:auto;overflow-x:hidden;min-height:0;
    scrollbar-width:thin;scrollbar-color:#DEE0E3 transparent}
  .twk-body::-webkit-scrollbar{width:8px}
  .twk-body::-webkit-scrollbar-track{background:transparent;margin:2px}
  .twk-body::-webkit-scrollbar-thumb{background:#DEE0E3;border-radius:4px;
    border:2px solid transparent;background-clip:content-box}
  .twk-body::-webkit-scrollbar-thumb:hover{background:#C9CDD4;
    border:2px solid transparent;background-clip:content-box}
  .twk-row{display:flex;flex-direction:column;gap:8px}
  .twk-row-h{flex-direction:row;align-items:center;justify-content:space-between;gap:10px}
  .twk-lbl{display:flex;justify-content:space-between;align-items:baseline;
    color:#1F2329}
  .twk-lbl>span:first-child{font-weight:500}
  .twk-val{color:#646A73;font-variant-numeric:tabular-nums}

  .twk-sect{font-size:11px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;
    color:#86909C;padding:10px 0 0}
  .twk-sect:first-child{padding-top:0}

  .twk-field{appearance:none;box-sizing:border-box;width:100%;min-width:0;height:32px;padding:0 10px;
    border:.5px solid #DEE0E3;border-radius:8px;
    background:#fff;color:inherit;font:inherit;outline:none}
  .twk-field:focus{border-color:#1F2329;box-shadow:0 0 0 2px rgba(31,35,41,.1)}
  select.twk-field{padding-right:24px;
    background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='%234E5969' d='M0 0h10L5 6z'/></svg>");
    background-repeat:no-repeat;background-position:right 10px center}

  .twk-slider{appearance:none;-webkit-appearance:none;width:100%;height:4px;margin:6px 0;
    border-radius:999px;background:#EBECEE;outline:none}
  .twk-slider::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;
    width:16px;height:16px;border-radius:50%;background:#fff;
    border:.5px solid #DEE0E3;box-shadow:0 1px 3px rgba(0,0,0,.16);cursor:default}
  .twk-slider::-moz-range-thumb{width:16px;height:16px;border-radius:50%;
    background:#fff;border:.5px solid #DEE0E3;box-shadow:0 1px 3px rgba(0,0,0,.16);cursor:default}

  .twk-seg{position:relative;display:flex;padding:2px;border-radius:8px;
    background:#EBECEE;user-select:none}
  .twk-seg-thumb{position:absolute;top:2px;bottom:2px;border-radius:6px;
    background:#fff;box-shadow:0 1px 3px rgba(0,0,0,.1);
    transition:left .15s cubic-bezier(.3,.7,.4,1),width .15s}
  .twk-seg.dragging .twk-seg-thumb{transition:none}
  .twk-seg button{appearance:none;position:relative;z-index:1;flex:1;border:0;
    background:transparent;color:#646A73;font:inherit;font-weight:500;min-height:28px;
    border-radius:6px;cursor:default;padding:4px 6px;line-height:1.2;
    overflow-wrap:anywhere;transition:color .15s}
  .twk-seg button[aria-checked="true"]{color:#1F2329}

  .twk-toggle{position:relative;width:36px;height:20px;border:0;border-radius:999px;
    background:#E4E6E8;transition:background .15s;cursor:default;padding:0}
  .twk-toggle[data-on="1"]{background:#1F2329}
  .twk-toggle i{position:absolute;top:2px;left:2px;width:16px;height:16px;border-radius:50%;
    background:#fff;box-shadow:0 1px 2px rgba(0,0,0,.2);transition:transform .15s}
  .twk-toggle[data-on="1"] i{transform:translateX(16px)}

  .twk-num{display:flex;align-items:center;box-sizing:border-box;min-width:0;height:32px;padding:0 0 0 10px;
    border:.5px solid #DEE0E3;border-radius:8px;background:#fff}
  .twk-num-lbl{font-weight:500;color:#646A73;cursor:ew-resize;
    user-select:none;padding-right:8px}
  .twk-num input{flex:1;min-width:0;height:100%;border:0;background:transparent;
    font:inherit;font-variant-numeric:tabular-nums;text-align:right;padding:0 10px 0 0;
    outline:none;color:inherit;-moz-appearance:textfield}
  .twk-num input::-webkit-inner-spin-button,.twk-num input::-webkit-outer-spin-button{
    -webkit-appearance:none;margin:0}
  .twk-num-unit{padding-right:10px;color:#86909C}

  .twk-btn{appearance:none;height:32px;padding:0 14px;border:0;border-radius:8px;
    background:#1F2329;color:#fff;font:inherit;font-weight:500;cursor:default}
  .twk-btn:hover{background:#333A44}
  .twk-btn.secondary{background:#F2F3F5;color:#1F2329}
  .twk-btn.secondary:hover{background:#E5E6EB}

  .twk-swatch{appearance:none;-webkit-appearance:none;width:56px;height:24px;
    border:.5px solid #DEE0E3;border-radius:6px;padding:0;cursor:default;
    background:transparent;flex-shrink:0}
  .twk-swatch::-webkit-color-swatch-wrapper{padding:0}
  .twk-swatch::-webkit-color-swatch{border:0;border-radius:5.5px}
  .twk-swatch::-moz-color-swatch{border:0;border-radius:5.5px}

  .twk-chips{display:flex;gap:8px}
  .twk-chip{position:relative;appearance:none;flex:1;min-width:0;height:40px;
    padding:0;border:0;border-radius:6px;overflow:hidden;cursor:default;
    box-shadow:0 0 0 .5px rgba(0,0,0,.08),0 1px 2px rgba(0,0,0,.05);
    transition:transform .12s cubic-bezier(.3,.7,.4,1),box-shadow .12s}
  .twk-chip:hover{transform:translateY(-1px);
    box-shadow:0 0 0 .5px rgba(0,0,0,.14),0 4px 10px rgba(0,0,0,.1)}
  .twk-chip[data-on="1"]{box-shadow:0 0 0 2px #fff,0 0 0 3.5px rgba(0,0,0,.85),
    0 2px 6px rgba(0,0,0,.15)}
  .twk-chip>span{position:absolute;top:0;bottom:0;right:0;width:34%;
    display:flex;flex-direction:column;box-shadow:-1px 0 0 rgba(0,0,0,.1)}
  .twk-chip>span>i{flex:1;box-shadow:0 -1px 0 rgba(0,0,0,.1)}
  .twk-chip>span>i:first-child{box-shadow:none}
  .twk-chip svg{position:absolute;top:6px;left:6px;width:13px;height:13px;
    filter:drop-shadow(0 1px 1px rgba(0,0,0,.3))}
`;

// ── useTweaks ───────────────────────────────────────────────────────────────
// Single source of truth for tweak values. setTweak persists via the host
// (miaoda:tweaks:set-keys → host rewrites the EDITMODE block on disk).
function useTweaks(defaults) {
  const [values, setValues] = React.useState(defaults);
  // Accepts either setTweak('key', value) or setTweak({ key: value, ... }) so a
  // useState-style call doesn't write a "[object Object]" key into the persisted
  // JSON block.
  const setTweak = React.useCallback((keyOrEdits, val) => {
    const edits = typeof keyOrEdits === 'object' && keyOrEdits !== null
      ? keyOrEdits : { [keyOrEdits]: val };
    setValues((prev) => ({ ...prev, ...edits }));
    window.parent.postMessage({ type: 'miaoda:tweaks:set-keys', edits }, '*');
    // Same-window signal so in-page listeners (deck-stage rail thumbnails)
    // can react — the parent message only reaches the host, not peers.
    window.dispatchEvent(new CustomEvent('tweakchange', { detail: edits }));
  }, []);
  return [values, setTweak];
}

// ── TweaksPanel ─────────────────────────────────────────────────────────────
// Floating shell. Registers the protocol listener BEFORE announcing
// availability — if the announce ran first, the host's activate could land
// before our handler exists and the toolbar toggle would silently no-op.
// The close button posts miaoda:tweaks:dismissed so the host's toolbar toggle
// flips off in lockstep; the host echoes miaoda:tweaks:deactivate back which
// is what actually hides the panel.
function TweaksPanel({ title = 'Tweaks', children }) {
  const [open, setOpen] = React.useState(false);
  const dragRef = React.useRef(null);
  // x = distance from the viewport's right edge, y = distance from its TOP.
  // The panel anchors top-right (right/top in CSS) so it clears the host's
  // bottom toolbar; both axes still grow inward, so clamp/drag math is
  // symmetric with the old bottom-right anchor.
  const offsetRef = React.useRef({ x: 16, y: 16 });
  const PAD = 16;

  const clampToViewport = React.useCallback(() => {
    const panel = dragRef.current;
    if (!panel) return;
    const w = panel.offsetWidth, h = panel.offsetHeight;
    const maxRight = Math.max(PAD, window.innerWidth - w - PAD);
    const maxTop = Math.max(PAD, window.innerHeight - h - PAD);
    offsetRef.current = {
      x: Math.min(maxRight, Math.max(PAD, offsetRef.current.x)),
      y: Math.min(maxTop, Math.max(PAD, offsetRef.current.y)),
    };
    panel.style.right = offsetRef.current.x + 'px';
    panel.style.top = offsetRef.current.y + 'px';
  }, []);

  React.useEffect(() => {
    if (!open) return;
    clampToViewport();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', clampToViewport);
      return () => window.removeEventListener('resize', clampToViewport);
    }
    const ro = new ResizeObserver(clampToViewport);
    ro.observe(document.documentElement);
    return () => ro.disconnect();
  }, [open, clampToViewport]);

  React.useEffect(() => {
    const onMsg = (e) => {
      const t = e?.data?.type;
      if (t === 'miaoda:tweaks:activate') setOpen(true);
      else if (t === 'miaoda:tweaks:deactivate') setOpen(false);
    };
    window.addEventListener('message', onMsg);
    window.parent.postMessage({ type: 'miaoda:tweaks:available' }, '*');
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const dismiss = () => {
    setOpen(false);
    window.parent.postMessage({ type: 'miaoda:tweaks:dismissed' }, '*');
  };

  const onDragStart = (e) => {
    const panel = dragRef.current;
    if (!panel) return;
    const r = panel.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY;
    const startRight = window.innerWidth - r.right;
    const startTop = r.top;
    const move = (ev) => {
      offsetRef.current = {
        x: startRight - (ev.clientX - sx),
        y: startTop + (ev.clientY - sy),
      };
      clampToViewport();
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  if (!open) return null;
  return (
    <>
      <style>{__TWEAKS_STYLE}</style>
      <div ref={dragRef} className="twk-panel" data-miaoda-chrome=""
           style={{ right: offsetRef.current.x, top: offsetRef.current.y }}>
        <div className="twk-hd" onMouseDown={onDragStart}>
          <b>{title}</b>
          <button className="twk-x" aria-label="Close tweaks"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={dismiss}>✕</button>
        </div>
        <div className="twk-body">
          {children}
        </div>
      </div>
    </>
  );
}

// ── Layout helpers ──────────────────────────────────────────────────────────

function TweakSection({ label, children }) {
  return (
    <>
      <div className="twk-sect">{label}</div>
      {children}
    </>
  );
}

function TweakRow({ label, value, children, inline = false }) {
  return (
    <div className={inline ? 'twk-row twk-row-h' : 'twk-row'}>
      <div className="twk-lbl">
        <span>{label}</span>
        {value != null && <span className="twk-val">{value}</span>}
      </div>
      {children}
    </div>
  );
}

// ── Controls ────────────────────────────────────────────────────────────────

function TweakSlider({ label, value, min = 0, max = 100, step = 1, unit = '', onChange }) {
  return (
    <TweakRow label={label} value={`${value}${unit}`}>
      <input type="range" className="twk-slider" min={min} max={max} step={step}
             value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </TweakRow>
  );
}

function TweakToggle({ label, value, onChange }) {
  return (
    <div className="twk-row twk-row-h">
      <div className="twk-lbl"><span>{label}</span></div>
      <button type="button" className="twk-toggle" data-on={value ? '1' : '0'}
              role="switch" aria-checked={!!value}
              onClick={() => onChange(!value)}><i /></button>
    </div>
  );
}

function TweakRadio({ label, value, options, onChange }) {
  const trackRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(false);
  // The active value is read by pointer-move handlers attached for the lifetime
  // of a drag — ref it so a stale closure doesn't fire onChange for every move.
  const valueRef = React.useRef(value);
  valueRef.current = value;

  // Segments wrap mid-word once per-segment width runs out. The track is
  // ~244px (280 panel − 32 body pad − 4 seg pad), each button loses 12px
  // to its own padding, and 12px system-ui averages ~6.6px/char — so 2
  // options fit ~16 chars each, 3 fit ~10. Past that (or >3 options), fall
  // back to a dropdown rather than wrap.
  const labelLen = (o) => String(typeof o === 'object' ? o.label : o).length;
  const maxLen = options.reduce((m, o) => Math.max(m, labelLen(o)), 0);
  const fitsAsSegments = maxLen <= ({ 2: 16, 3: 10 }[options.length] ?? 0);
  if (!fitsAsSegments) {
    // <select> emits strings — map back to the original option value so the
    // fallback stays type-preserving (numbers, booleans) like the segment path.
    const resolve = (s) => {
      const m = options.find((o) => String(typeof o === 'object' ? o.value : o) === s);
      return m === undefined ? s : typeof m === 'object' ? m.value : m;
    };
    return <TweakSelect label={label} value={value} options={options}
                        onChange={(s) => onChange(resolve(s))} />;
  }
  const opts = options.map((o) => (typeof o === 'object' ? o : { value: o, label: o }));
  const idx = Math.max(0, opts.findIndex((o) => o.value === value));
  const n = opts.length;

  const segAt = (clientX) => {
    const r = trackRef.current.getBoundingClientRect();
    const inner = r.width - 4;
    const i = Math.floor(((clientX - r.left - 2) / inner) * n);
    return opts[Math.max(0, Math.min(n - 1, i))].value;
  };

  const onPointerDown = (e) => {
    setDragging(true);
    const v0 = segAt(e.clientX);
    if (v0 !== valueRef.current) onChange(v0);
    const move = (ev) => {
      if (!trackRef.current) return;
      const v = segAt(ev.clientX);
      if (v !== valueRef.current) onChange(v);
    };
    const up = () => {
      setDragging(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <TweakRow label={label}>
      <div ref={trackRef} role="radiogroup" onPointerDown={onPointerDown}
           className={dragging ? 'twk-seg dragging' : 'twk-seg'}>
        <div className="twk-seg-thumb"
             style={{ left: `calc(2px + ${idx} * (100% - 4px) / ${n})`,
                      width: `calc((100% - 4px) / ${n})` }} />
        {opts.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={o.value === value}>
            {o.label}
          </button>
        ))}
      </div>
    </TweakRow>
  );
}

function TweakSelect({ label, value, options, onChange }) {
  return (
    <TweakRow label={label}>
      <select className="twk-field" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => {
          const v = typeof o === 'object' ? o.value : o;
          const l = typeof o === 'object' ? o.label : o;
          return <option key={v} value={v}>{l}</option>;
        })}
      </select>
    </TweakRow>
  );
}

function TweakText({ label, value, placeholder, onChange }) {
  return (
    <TweakRow label={label}>
      <input className="twk-field" type="text" value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)} />
    </TweakRow>
  );
}

function TweakNumber({ label, value, min, max, step = 1, unit = '', onChange }) {
  const clamp = (n) => {
    if (min != null && n < min) return min;
    if (max != null && n > max) return max;
    return n;
  };
  const startRef = React.useRef({ x: 0, val: 0 });
  const onScrubStart = (e) => {
    e.preventDefault();
    startRef.current = { x: e.clientX, val: value };
    const decimals = (String(step).split('.')[1] || '').length;
    const move = (ev) => {
      const dx = ev.clientX - startRef.current.x;
      const raw = startRef.current.val + dx * step;
      const snapped = Math.round(raw / step) * step;
      onChange(clamp(Number(snapped.toFixed(decimals))));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  return (
    <div className="twk-num">
      <span className="twk-num-lbl" onPointerDown={onScrubStart}>{label}</span>
      <input type="number" value={value} min={min} max={max} step={step}
             onChange={(e) => onChange(clamp(Number(e.target.value)))} />
      {unit && <span className="twk-num-unit">{unit}</span>}
    </div>
  );
}

// Relative-luminance contrast pick — checkmarks drawn over a swatch need to
// read on both #111 and #fafafa without per-option configuration. Hex input
// only (#rgb / #rrggbb); named or rgb()/hsl() colors fall through to "light".
function __twkIsLight(hex) {
  const h = String(hex).replace('#', '');
  const x = h.length === 3 ? h.replace(/./g, (c) => c + c) : h.padEnd(6, '0');
  const n = parseInt(x.slice(0, 6), 16);
  if (Number.isNaN(n)) return true;
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return r * 299 + g * 587 + b * 114 > 148000;
}

const __TwkCheck = ({ light }) => (
  <svg viewBox="0 0 14 14" aria-hidden="true">
    <path d="M3 7.2 5.8 10 11 4.2" fill="none" strokeWidth="2.2"
          strokeLinecap="round" strokeLinejoin="round"
          stroke={light ? 'rgba(0,0,0,.78)' : '#fff'} />
  </svg>
);

// TweakColor — curated color/palette picker. Each option is either a single
// hex string or an array of 1-5 hex strings; the card adapts — a lone color
// renders solid, a palette renders colors[0] as the hero (left ~2/3) with the
// rest stacked in a sharp column on the right. onChange emits the
// option in the shape it was passed (string stays string, array stays array).
// Without options it falls back to the native color input for back-compat.
function TweakColor({ label, value, options, onChange }) {
  if (!options || !options.length) {
    return (
      <div className="twk-row twk-row-h">
        <div className="twk-lbl"><span>{label}</span></div>
        <input type="color" className="twk-swatch" value={value}
               onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }
  // Native <input type=color> emits lowercase hex per the HTML spec, so
  // compare case-insensitively. String() guards JSON.stringify(undefined),
  // which returns the primitive undefined (no .toLowerCase).
  const key = (o) => String(JSON.stringify(o)).toLowerCase();
  const cur = key(value);
  return (
    <TweakRow label={label}>
      <div className="twk-chips" role="radiogroup">
        {options.map((o, i) => {
          const colors = Array.isArray(o) ? o : [o];
          const [hero, ...rest] = colors;
          const sup = rest.slice(0, 4);
          const on = key(o) === cur;
          return (
            <button key={i} type="button" className="twk-chip" role="radio"
                    aria-checked={on} data-on={on ? '1' : '0'}
                    aria-label={colors.join(', ')} title={colors.join(' · ')}
                    style={{ background: hero }}
                    onClick={() => onChange(o)}>
              {sup.length > 0 && (
                <span>
                  {sup.map((c, j) => <i key={j} style={{ background: c }} />)}
                </span>
              )}
              {on && <__TwkCheck light={__twkIsLight(hero)} />}
            </button>
          );
        })}
      </div>
    </TweakRow>
  );
}

function TweakButton({ label, onClick, secondary = false }) {
  return (
    <button type="button" className={secondary ? 'twk-btn secondary' : 'twk-btn'}
            onClick={onClick}>{label}</button>
  );
}

// Opt out of DCViewport's transform so position:fixed works against the viewport.
TweaksPanel.dcOverlay = true;

Object.assign(window, {
  useTweaks, TweaksPanel, TweakSection, TweakRow,
  TweakSlider, TweakToggle, TweakRadio, TweakSelect,
  TweakText, TweakNumber, TweakColor, TweakButton,
});

// ── TweakSuggestionBar (flag-gated addon) ───────────────────────────────────
(function () {
  const s = document.createElement('style');
  s.textContent = `
    @keyframes twk-blink{50%{opacity:0}}
    @keyframes twk-fadein{from{opacity:0;transform:translateX(4px)}to{opacity:1;transform:none}}
    .twk-sugg{display:flex;align-items:center;gap:6px;padding:5px 8px;border-radius:8px;
      background:#F7F8FA;border:.5px solid #EBECEE;transition:all .15s}
    .twk-sugg:focus-within{background:#F2F3F5;border-color:#DEE0E3}
    .twk-sugg-field{position:relative;flex:1;min-width:0}
    .twk-sugg-field input{width:100%;height:20px;border:0;background:transparent;
      font:inherit;outline:none;color:inherit}
    .twk-sugg-ghost{position:absolute;inset:0;display:flex;align-items:center;
      color:#86909C;pointer-events:none;white-space:nowrap;overflow:hidden}
    .twk-sugg-ghost.hint{color:#A9AEB8}
    .twk-sugg-caret{display:inline-block;width:1px;height:13px;margin-left:1px;
      border-right:1.5px solid currentColor;opacity:.5;animation:twk-blink 1s step-end infinite}
    .twk-sugg-ideas{appearance:none;border:0;background:transparent;font:inherit;
      font-size:10.5px;font-weight:600;color:#646A73;cursor:default;padding:0 2px;
      white-space:nowrap;animation:twk-fadein .25s ease}
    .twk-sugg-ideas:hover{color:#1F2329}
    .twk-sugg-ideas svg{color:#D97757}
    .twk-sugg-send{appearance:none;border:0;height:20px;padding:0 8px;border-radius:5px;
      background:#1F2329;color:#fff;font:inherit;font-size:10px;font-weight:600;cursor:default}
  `;
  document.head.appendChild(s);
})();

const __twkSendChat = (text) =>
  window.parent.postMessage({ type: 'miaoda:tweaks:chat', text }, '*');

const __TWK_SPARK_PATH = 'M18.3658 62.2435L36.7823 51.9165L37.0858 51.012L36.7823 50.5083H35.8716L32.7853 50.3206L22.2616 50.0389L13.1546 49.6634L4.30054 49.194L2.07438 48.7246L0 45.9551L0.202378 44.5938L2.07438 43.3264L4.75589 43.5611L10.6755 43.9836L19.5801 44.5938L26.0056 44.9693L35.568 45.9551H37.0858L37.2882 45.3448L36.7823 44.9693L36.3775 44.5938L27.1693 38.3507L17.2022 31.7789L11.9909 27.9767L9.20822 26.0522L7.79157 24.2684L7.18443 20.3254L9.71416 17.5089L13.1546 17.7436L14.0147 17.9783L17.5057 20.654L24.9431 26.4277L34.6573 33.5627L36.0739 34.7362L36.6444 34.3512L36.7317 34.079L36.0739 32.9994L30.8121 23.4704L25.1961 13.7537L22.6664 9.71675L22.0086 7.32277C21.7539 6.31812 21.6039 5.48695 21.6039 4.45938L24.4878 0.516349L26.1068 0L30.0026 0.516349L31.6216 1.92457L34.0502 7.46359L37.9459 16.1476L44.0173 27.9767L45.7881 31.4973L46.7494 34.7362L47.1036 35.722H47.7107V35.1587L48.2166 28.4931L49.1274 20.3254L50.0381 9.81063L50.3416 6.85336L51.8089 3.28586L54.7434 1.36128L57.0201 2.44092L58.8921 5.11655L58.6391 6.85336L57.5261 14.0822L55.3505 25.395L53.9338 32.9994H54.7434L55.7047 32.0136L59.5498 26.944L65.9753 18.8702L68.8086 15.6782L72.1479 12.1577L74.2729 10.4678H78.3204L81.2549 14.8802L79.9395 19.4335L75.7907 24.6909L72.3503 29.1503L67.4173 35.7593L64.3563 41.0732L64.6308 41.5116L65.3682 41.4487L76.499 39.0548L82.5198 37.9751L89.7042 36.7547L92.9423 38.2568L93.2964 39.8058L92.0316 42.9509L84.3412 44.8285L75.3354 46.6592L61.9245 49.8162L61.776 49.9356L61.9513 50.1956L67.9991 50.743L70.5795 50.8839H76.9038L88.6923 51.7757L91.7786 53.7942L93.6 56.282L93.2964 58.2066L88.5405 60.6006L82.1656 59.0985L67.2402 55.531L62.1302 54.2636H61.4218V54.6861L65.6718 58.8638L73.514 65.9049L83.2787 75.0114L83.7846 77.2646L82.5198 79.0483L81.2043 78.8606L72.6032 72.3827L69.264 69.4724L61.776 63.1354H61.2701V63.7926L62.9903 66.3274L72.1479 80.081L72.6032 84.3057L71.9455 85.667L69.5676 86.5119L66.9872 86.0425L61.5736 78.4851L56.0588 70.0357L51.6065 62.4313L51.0687 62.7708L48.419 91.0652L47.2048 92.5204L44.3715 93.6L41.9935 91.8162L40.7286 88.9059L41.9935 83.1322L43.5114 75.6217L44.7256 69.6602L45.8387 62.2435L46.5185 59.7659L46.4584 59.6001L45.9153 59.6914L40.3239 67.3601L31.824 78.8606L25.0949 86.0425L23.4759 86.6997L20.6932 85.2445L20.9462 82.6628L22.5146 80.3627L31.824 68.5336L37.44 61.1639L41.0595 56.9335L41.0243 56.3216L40.8245 56.3046L16.0891 72.4297L11.6874 72.993L9.76476 71.2092L10.0177 68.2989L10.9284 67.3601L18.3658 62.2435Z';

function ClaudeSpark({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 94 94" fill="currentColor"
         style={{ display: 'inline-block', verticalAlign: '-1px' }}>
      <path d={__TWK_SPARK_PATH} />
    </svg>
  );
}

// Typewriter-cycles through `suggestions`. Clicking the field while a
// suggestion is animating freezes it as ghost text; Tab accepts it into the
// input. Enter posts miaoda:tweaks:chat (host drops the text into the chat
// composer for the user to send). After the cycle the static placeholder
// types in and "Ideas" appears — clicking asks for three more suggestions.
function TweakSuggestionBar({
  suggestions = [],
  placeholder = 'Describe a tweak…',
  ideasPrompt = 'Suggest three more tweak ideas for this design and update the suggestions on TweakSuggestionBar.',
}) {
  const [val, setVal] = React.useState('');
  const [ghost, setGhost] = React.useState('');
  const [focused, setFocused] = React.useState(false);
  const inputRef = React.useRef(null);
  const tw = useTwkTypewriter(suggestions, { placeholder, enabled: !val && !ghost && !focused });

  const freeze = () => {
    tw.markPlayed();
    if (val || ghost) return;
    const target = !tw.done ? suggestions[tw.idx] : '';
    if (target) setGhost(target);
    inputRef.current?.focus();
  };

  const submit = () => {
    const v = (val || ghost).trim();
    if (!v) return;
    __twkSendChat(v);
    setVal('');
    setGhost('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Tab' && ghost && !val) {
      e.preventDefault();
      setVal(ghost);
      setGhost('');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    } else if (e.key === 'Escape') {
      setGhost('');
    }
  };

  const requestIdeas = () => __twkSendChat(ideasPrompt);

  const showAnim = !val && !ghost && !focused && !tw.done;
  const showStatic = !val && !ghost && !focused && tw.done;

  return (
    <div className="twk-sugg" onMouseDown={freeze}>
      <div className="twk-sugg-field">
        <input
          ref={inputRef}
          value={val}
          placeholder={focused && !ghost ? placeholder : ''}
          onChange={(e) => { setVal(e.target.value); setGhost(''); }}
          onFocus={() => { setFocused(true); tw.markPlayed(); }}
          onBlur={() => { setFocused(false); if (!val) setGhost(''); }}
          onKeyDown={onKeyDown}
        />
        {showAnim && (
          <div className="twk-sugg-ghost">
            {tw.text}<span className="twk-sugg-caret" />
          </div>
        )}
        {showStatic && (
          <div className="twk-sugg-ghost">
            {tw.tail}{tw.tail.length < placeholder.length && <span className="twk-sugg-caret" />}
          </div>
        )}
        {ghost && !val && (
          <div className="twk-sugg-ghost hint">{ghost}</div>
        )}
      </div>
      {val || ghost ? (
        <button className="twk-sugg-send"
                onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                onClick={submit}>
          Add
        </button>
      ) : tw.done && !focused ? (
        <button className="twk-sugg-ideas"
                onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                onClick={requestIdeas}>
          Ideas <ClaudeSpark />
        </button>
      ) : null}
    </div>
  );
}

// Minimal type→pause→erase cycler. Plays once per unique `items` content per
// session — a reload from a tweak-value write skips straight to done; a new
// suggestion set (after "Ideas") gets a fresh animation.
function useTwkTypewriter(items, { placeholder, typeMs = 35, eraseMs = 22, pauseMs = 1800, enabled = true } = {}) {
  const key = React.useMemo(() => '__twk_played:' + JSON.stringify(items), [items.join('\n')]);
  const played = () => { try { return sessionStorage.getItem(key) === '1'; } catch { return false; } };

  const [text, setText] = React.useState('');
  const [tail, setTail] = React.useState(() => (items.length === 0 || played() ? placeholder : ''));
  const [idx, setIdx] = React.useState(0);
  const [done, setDone] = React.useState(() => items.length === 0 || played());
  const phase = React.useRef('type');
  const n = React.useRef(0);

  const markPlayed = React.useCallback(() => {
    try { sessionStorage.setItem(key, '1'); } catch {}
    setDone(true);
  }, [key]);

  React.useEffect(() => {
    const skip = items.length === 0 || played();
    setText(''); setIdx(0);
    setDone(skip);
    setTail(skip ? placeholder : '');
    phase.current = 'type'; n.current = 0;
  }, [key]);

  React.useEffect(() => {
    if (done || !enabled) return;
    const item = items[idx] ?? '';
    let t;
    const tick = () => {
      if (phase.current === 'type') {
        n.current++;
        setText(item.slice(0, n.current));
        if (n.current >= item.length) { phase.current = 'pause'; t = setTimeout(tick, pauseMs); }
        else t = setTimeout(tick, typeMs + Math.random() * 20);
      } else if (phase.current === 'pause') {
        phase.current = 'erase'; t = setTimeout(tick, eraseMs);
      } else {
        n.current--;
        setText(item.slice(0, n.current));
        if (n.current <= 0) {
          if (idx === items.length - 1) { markPlayed(); return; }
          phase.current = 'type'; setIdx((i) => i + 1);
        } else t = setTimeout(tick, eraseMs);
      }
    };
    phase.current = 'type'; n.current = 0; setText('');
    t = setTimeout(tick, 400);
    return () => clearTimeout(t);
  }, [idx, done, key, enabled, typeMs, eraseMs, pauseMs]);

  React.useEffect(() => {
    if (!done || tail === placeholder) return;
    let i = 0;
    const t = setInterval(() => {
      i++; setTail(placeholder.slice(0, i));
      if (i >= placeholder.length) clearInterval(t);
    }, 28);
    return () => clearInterval(t);
  }, [done, placeholder]);

  return { text, tail, idx, done, markPlayed };
}

Object.assign(window, { TweakSuggestionBar });

/* ============================================================
   data.jsx — 李洲昊 真实简历内容 + 共享 SVG 图标
   Exports to window: PORTFOLIO, Icon
   ============================================================ */

const A = (f) => "assets/" + f; // 本地真实截图
const AVATAR = A("avatar.jpg");

const PORTFOLIO = {
  name: "李洲昊",
  handle: "Li Zhouhao",
  role: "AI 产品经理 · Agent 方向",
  location: "北京 · 传播学硕士",
  avatar: AVATAR,

  hero: {
    kicker: "AI 产品 / Agent · 智能传播",
    title2: "在做的，是让 AI 真正“把事办成”。",
    statement:
      "传播学硕士，专注 <strong>AI 产品与 Agent 落地</strong>。实习足迹覆盖豆包工作 & Aily、京东 JoyCode、百度客悦与智谱 AMiner，习惯<strong>用数据说话、用原型对话</strong>。",
    stickers: [
      { text: "豆包工作 & Aily", color: "var(--orange)", x: 1,  y: 6,  rot: -7 },
      { text: "4 段大厂实习", color: "var(--blue)",  x: 58, y: 1,  rot: 5 },
      { text: "3 款独立产品", color: "var(--mint)",  x: 62, y: 74, rot: -4 },
      { text: "把想法跑通", color: "var(--pink)",  x: -3, y: 70, rot: 6 },
    ],
  },

  marquee: [
    "智能传播", "多模态 Agent", "vibe coding", "端到端智能交付",
    "AgentScope", "LangGraph", "认知神经传播", "Prompt 工程", "评测体系", "内容 Agent",
  ],

  stats: [
    { num: "4", label: "段大厂实习", color: "var(--orange)" },
    { num: "3", label: "款独立产品", color: "var(--blue)" },
    { num: "3.84", label: "本科 GPA / 4.0", color: "var(--mint)" },
  ],

  about: [
    "我是李洲昊，一名专注 AI 产品与 Agent 方向的产品经理，也是北京师范大学传播学硕士。相信好产品是“被使用出来的”，喜欢把复杂问题拆成可以被验证的小假设。",
    "从豆包工作 & Aily 的办公智能体，到京东 JoyCode 的端到端交付 Agent、百度客悦的内容营销数字员工、智谱 AMiner 的学术工具——我习惯用数据说话、用原型对话、用评测兜底能力边界。",
  ],
  aboutLead: "把模糊的想法，变成能被信任、每天被使用的产品。",

  education: [
    {
      school: "北京师范大学",
      degree: "传播学 · 硕士",
      period: "2024 — 2027",
      detail: "主修智能传播应用与理论、计算传播研究、认知神经传播学方法与技术。研究方向：<strong>智能传播、认知神经传播</strong>。",
      img: A("bnu.jpg"),
      color: "var(--blue)",
    },
    {
      school: "中国传媒大学",
      degree: "传播学 · 本科",
      period: "2020 — 2024",
      detail: "主修传播学概论、统计学、传播研究方法、传播效果实证研究、多媒体信息设计、传播心理学等；<strong>GPA 3.84 / 4.00</strong>。",
      img: A("cuc.jpg"),
      color: "var(--orange)",
    },
  ],

  experience: [
    {
      id: "aily",
      company: "字节跳动",
      short: "字节 · 豆包工作 & Aily",
      team: "豆包工作 & Aily",
      role: "AI 产品实习生",
      period: "2026/07 — 至今",
      current: true,
      color: "var(--orange)",
      summary: "面向工作场景拓展<strong>豆包工作专业 Agent 应用</strong>，解决垂直能力触达、飞书内上下文分散及多 Agent 协作可控性问题。",
      work: [
        {
          "label": "工作伙伴接入设计",
          "body": "负责豆包工作 App 工作伙伴模块，设计工作伙伴对话交互以及子 Agent 调用全流程；以 <strong>A2A 协议</strong>打通飞书内既有 Agent 矩阵，用户可在当前对话中按需唤起专业能力，免于在不同入口间查找切换，降低任务启动成本。"
        },
        {
          "label": "评测与效果优化",
          "body": "从任务结果和执行过程定位问题，参与网页、应用生成等场景的端到端评测，构建评测集以及基于 Rubric 的 <strong>6 层门禁 + 6 层效果评测标准</strong>；针对工作伙伴任务耗时长、额度不透明的反馈，通过 Trace 排查问题工具及 Skills。"
        },
        {
          "label": "体验优化需求",
          "body": "针对工作伙伴网页端高频体验痛点（任务归档与置顶、收件箱降噪、资源库删除、附件添加与选择），明确消息接收范围与豁免规则、定义置顶/归档状态限制与互斥关系、界定删除权限与可逆路径，从源头减少用户打扰与误操作成本。"
        }
      ],
      thinking: [
        "<strong>办公入口的竞争：</strong>我仍然认为，个人办公与企业协作的需求边界会继续交叠，但两者对权限、责任和执行确认的要求并不相同。对话框适合表达临时意图，项目和工作台适合承接持续任务，群聊则提供协作上下文。产品需要让这些入口围绕同一件事衔接，避免用户每换一个入口就重新交代背景。",
        "<strong>专业能力如何被使用：</strong>Agent 数量增加后，选择本身会成为用户负担。@伙伴让用户能够显式选择专业能力，也保留了对任务交给谁的控制感。我的判断是，明确意图时应缩短调用路径，意图模糊时再提供推荐；如果过早把路由完全交给系统，用户一旦遇到错误，就很难理解该换伙伴还是修改任务。",
        "<strong>信任决定授权深度：</strong>执行过程透明、结果可信、成本可预期，决定用户愿意交给 Agent 多大的任务。工作台中的任务拆解、阶段产物和状态信息，应优先帮助用户判断是否继续等待、补充信息或接管。界面不需要展示所有内部步骤，但必须在影响结果和成本的节点提供解释与控制。",
        "<strong>企业上下文的长期价值：</strong>沿着此前对组织工作流的判断，我更看重 Agent 是否理解信息的来源、时效、权限和任务归属。单纯接入更多数据源可能增加噪声；能把沟通记录转成任务依据，并在需要时向合适的人确认，才更有机会持续进入组织的日常工作。"
      ],
      metrics: [
        {
          "v": "8000+",
          "l": "入口改造上线后工作日日均活跃"
        },
        {
          "v": "5.19 次",
          "l": "人均对话"
        },
        {
          "v": "100%",
          "l": "端到端评测合格率"
        },
        {
          "v": "96.7%",
          "l": "信息无损传输率"
        }
      ],
      visualGroups: [
        {
          title: "豆包工作伙伴",
          visuals: [
            { img: A("aily-1.png"), label: "豆包工作伙伴工作台", caption: "把工作台从单轮问答改造为任务台的产品形态。" },
            { img: A("aily-2.png"), label: "自动化任务", caption: "自动化任务的配置与执行界面。" },
            { img: A("aily-3.png"), label: "智能体", caption: "智能体的对话与任务执行界面。" },
            { img: A("aily-4.png"), label: "智能体小队协作", caption: "智能体小队协作模式的产品界面。" },
            { img: A("aily-5.png"), label: "专家与技能市场", caption: "专家与技能市场的产品界面。" },
          ],
        },
        {
          title: "豆包工作",
          visuals: [
            { img: A("doubaowork-1.png"), label: "豆包工作首页", caption: "豆包工作的任务入口、项目与技能连接界面。" },
            { img: A("doubaowork-2.png"), label: "伙伴与小队", caption: "工作伙伴、小队与快捷任务的协作入口。" },
            { img: A("doubaowork-3.png"), label: "伙伴管理", caption: "工作伙伴的管理、搜索与召唤界面。" },
            { img: A("doubaowork-4.png"), label: "伙伴任务台", caption: "围绕单个工作伙伴组织任务、自动化与产物。" },
            { img: A("doubaowork-5.png"), label: "伙伴详情", caption: "工作伙伴的人设、技能与安全配置界面。" },
          ],
        },
      ],
    },
    {
      id: "joycode",
      company: "京东科技",
      short: "京东 · JoyCode",
      team: "京东云事业群",
      role: "AI 产品实习生",
      period: "2026/03 — 2026/07",
      color: "var(--blue)",
      summary: "基于 <strong>JoyCode Team</strong> 实现端到端智能交付能力，从 0.5 到 1 解决 AI 辅助研发流程从单点提效走向组织级提效的问题。",
      work: [
        {
          "label": "交付流程设计",
          "body": "基于 JoyCode Team 参与端到端智能交付 Agent 设计，结合产品 Wiki 与 CodeWiki 补充业务和代码上下文，将需求分析、PRD、原型、TRD 与测试用例组织为连续产物，提供给下游 IDE 消费。TRD 设计兼顾单仓与跨仓库场景，关注上游需求能否转化为研发可继续使用的上下文。"
        },
        {
          "label": "溯源与节点验证",
          "body": "构建<strong>以 Cue 为单位的溯源凭证</strong>，使交付产物能够回到原始需求、业务上下文与代码依据。在产物流转节点采用 <strong>LLM-as-Judge + 代码引用验证</strong>检查准确性，为定位错误、审查产物与人工接管保留依据。"
        },
        {
          "label": "Harness 运行机制",
          "body": "基于 <strong>AgentScope.js</strong> 设计 Harness 运行机制，覆盖任务上下文管理、压缩与记忆、Planner、多 Agent 协作及原子能力调用。围绕长链路任务中的信息保留和分工，约束各阶段输入输出与工具调用边界，在实现成本、扩展性和交付稳定性之间做取舍。"
        },
        {
          "label": "评测与 Prompt 调优",
          "body": "主导 AI 生成效果评测，以真实研发需求分场景构建评测集，分别考察<strong>输出质量、实际可用性与业务采纳率</strong>。结合 badcase 定位问题，利用业务样例调整上下文供给、结构化 Prompt 与 Few-shot 策略，并通过回归验证效果、耗时和 Token 消耗。产物真实业务可用率为 <strong>85.2%</strong>，采纳率为 <strong>74.1%</strong>；流程覆盖 <strong>19 个团队</strong>，需求交付周期平均缩短 <strong>33.52%</strong>。"
        }
      ],
      thinking: [
        "<strong>组织提效的切入点：</strong>交付类 Agent 容易生成完整的文档，却留下大量核对和返工。我延续此前对业务可用性与采纳率的重视：优先解决需求、产品和研发交接中的信息损耗，让下一位使用者少做一次解释、查找或确认。自动生成环节的增加，只有在整条交付链路的成本下降时才有价值。",
        "<strong>产品边界与既有习惯：</strong>研发团队已经有 IDE 和协作流程，要求他们迁移到新工作台会增加采用成本。把产物交给下游 IDE 消费，是让 Agent 进入既有工作方式的一种选择。我会把体验重点放在上下文质量、引用依据和修改成本上，再判断哪些环节值得形成独立界面。",
        "<strong>可用与采纳之间：</strong>可用率和采纳率衡量不同问题，不能直接用两者的数值差推算流失。即使产物通过质量检查，用户仍可能因验证负担、接入时机或工作习惯而放弃使用。我会沿着实际使用过程找阻力：若核对依据很费力，就优先改善溯源；若产物到得太晚，就调整触发节点，而非继续堆生成能力。",
        "<strong>自动化程度的取舍：</strong>复杂任务需要规划弹性，稳定交付又依赖清晰边界。我的策略判断是，将高频、可验证的环节做成稳定流程，把开放探索留给 Agent，并在影响范围较大的节点保留人工确认。评估时还要计算审查和返工成本，防止局部生成加速掩盖下游负担。"
      ],
      metrics: [
        {
          "v": "85.2%",
          "l": "真实业务产物可用率"
        },
        {
          "v": "74.1%",
          "l": "产物采纳率"
        },
        {
          "v": "19 个",
          "l": "流程覆盖团队"
        },
        {
          "v": "33.52%",
          "l": "需求交付周期平均缩短"
        }
      ],
      visuals: [{ img: A("joycode-team.png"), label: "JoyCode Team 外部开放版", caption: "面向团队协作的端到端智能交付界面。" }],
    },
    {
      id: "keyoo",
      company: "百度",
      short: "百度 · 客悦",
      team: "智能云事业群",
      role: "Agent 开发实习生",
      period: "2025/12 — 2026/03",
      color: "var(--pink)",
      summary: "针对营销图文生产周期长、选题效率低及图文质量不稳定的问题，<strong>从 0 到 1 开发客悦数字员工弈小念</strong>，项目后续整合为 Hogee。",
      work: [
        {
          "label": "内容策略与 MVP",
          "body": "针对营销图文生产周期长、选题效率低和质量不稳定的问题，从 0 到 1 开发客悦数字员工弈小念，并用 Python 跑通本地 MVP。围绕小红书的选题、封面、标题、正文结构、种草语气和评论反馈拆解内容策略，按账号冷启、增长、稳定阶段提出渐进式路径，先解决稳定产出，再考虑风格迁移与反馈优化；项目后续整合为 Hogee。"
        },
        {
          "label": "工作流与上下文",
          "body": "将生产流程拆解为<strong>爆点抓取、内容提炼、选题生成、图文生成与内容质检</strong>五个阶段，基于 <strong>LangGraph</strong> 区分系统指令、品牌信息和附件。围绕品牌知识、产品卖点、历史爆文与图片素材组织生成流程，在指令中拆开品牌约束、平台语气、内容结构与禁区规则。"
        },
        {
          "label": "RAG 检索优化",
          "body": "在 RAG 链路引入 <strong>BM25 + KNN 多路召回与 RRF 重排</strong>，兼顾品牌名、产品词的精确匹配与语义相关信息检索，优化生成所需信息的供给。召回率提升 <strong>17.9%</strong>，Top-100 精确率提升 <strong>30.6%</strong>。"
        },
        {
          "label": "多模态生成与评测",
          "body": "针对海报、素人实拍图、Meme 图中 AI 特征明显、同质化、偏离品牌风格的问题，结合 CoT、Few-shot 与人工反馈样例调优。使用 <strong>Seedream 4.5/5.0</strong>，基于 <strong>DPG-Bench、GenEval、Compbench</strong> 评测，并结合内容准确性、物理一致性、画面质感、布局与创意等维度定位缺陷。内容可用率由 <strong>21% 提升至 74%</strong>，POC 阶段曝光 <strong>1000 万+</strong>。"
        }
      ],
      thinking: [
        "<strong>先确定服务哪一阶段：</strong>冷启动账号缺少素材和反馈，成熟账号更在意风格延续与内容差异。如果把所有用户放进同一套生成流程，模板很容易变成限制。此前按账号阶段拆策略的思路仍然成立：先让用户稳定完成一次生产，再根据其内容积累和经营目标引入更复杂的能力。",
        "<strong>质量标准需要有层次：</strong>内容 Agent 的难点在于，少数惊艳样例并不能保证批量可用。我会把事实、品牌与物理一致性作为必须守住的约束，将语气、风格和创意保留为可选择的空间。全部维度压成一个总分，可能让严重错误被其他高分掩盖，也可能把有价值的风格差异一并抹平。",
        "<strong>评测要能改变下一步动作：</strong>延续此前按缺陷反推迭代的判断，评测的价值在于指向具体干预。品牌信息缺失应先查检索与上下文，构图偏离应检查指令和生成约束，同质化则需要审视选题与样例分布。每类问题对应不同改动，才能避免所有 badcase 最后都变成加长 Prompt。",
        "<strong>生产价值与传播价值：</strong>内容可用率提高可以减少编辑返工，曝光还受到账号、选题、发布时间和分发影响。产品策略上，我会先把可控的生产效率和质量做稳定，再通过持续反馈探索哪些内容更适合目标受众；否则容易为了短期曝光追逐单一模板，削弱品牌的长期辨识度。"
      ],
      metrics: [
        {
          "v": "+17.9%",
          "l": "召回率提升"
        },
        {
          "v": "+30.6%",
          "l": "Top-100 精确率提升"
        },
        {
          "v": "21% → 74%",
          "l": "内容可用率"
        },
        {
          "v": "1000 万+",
          "l": "POC 阶段曝光"
        }
      ],
      visuals: [
        { img: A("keyoo-1.jpg"), label: "客悦 Keyoo", caption: "内容营销数字员工的早期产品形态。" },
        { img: A("hogee-1.png"), label: "Hogee 整合形态", caption: "成果整合进 Hogee 后的产品界面。" },
        { img: A("hogee-2.jpg"), label: "Hogee 工作台", caption: "成果整合进 Hogee 后的工作台界面。" },
      ],
    },
    {
      id: "aminer",
      company: "智谱华章",
      short: "智谱 · AMiner",
      team: "工程院",
      role: "AI 产品实习生",
      period: "2025/06 — 2025/10",
      color: "var(--mint)",
      summary: "AI 学术产品 <strong>AMiner</strong> 提供信息检索服务，但用户获取论文后续链路存在断裂，需要使阅读积累能够支持后续研究产出。",
      work: [
        {
          "label": "学术空间设计",
          "body": "针对用户获取论文后仍需跨平台阅读、整理和消化的问题，设计<strong>学术空间</strong>的 PRD 与交互流程，集中沉淀知识库、笔记，以及关注的学者、期刊和领域等研究资产。围绕论文阅读、摘要理解、笔记沉淀与后续研究产出组织体验，减少资料散落造成的上下文切换。"
        },
        {
          "label": "Research Labs",
          "body": "参考 NotebookLM 设计 <strong>Research Labs</strong>，以笔记本为单位承接 AI 多源检索、信息整合与多模态内容输出，使资料管理能够与具体研究任务衔接，并支持共享。上线一周内，学术空间与 Research Labs 的功能使用率分别为 <strong>22.1%</strong>、<strong>31.4%</strong>；产品整体七日留存率由 <strong>17.9% 提升至 21.4%</strong>。"
        },
        {
          "label": "商业化与使用支持",
          "body": "完成 AI 学术工具付费墙、会员权益与商业模式的竞品调研，对比文献检索、AI 阅读、知识库和写作辅助等功能的付费边界；同步输出标准化用户帮助手册，帮助用户理解功能与使用路径，为后续转化提供内容支持。"
        },
        {
          "label": "内容增长",
          "body": "围绕 AI 前沿进展、技术论文解读和工具教程搭建内容矩阵，以具体研究问题和使用场景吸引目标用户。内容与产品功能相衔接，关注读者在获取信息后能否进一步开展检索、阅读和整理。"
        }
      ],
      thinking: [
        "<strong>留存来自研究的连续性：</strong>此前把论文转化为个人知识资产的判断仍是核心。检索解决一次信息需求，研究却会持续数周甚至数月；产品需要让用户回来时能接着上次的问题继续工作。保存资料只是第一步，笔记、引用和研究任务之间能否重新建立联系，才决定积累是否有用。",
        "<strong>两个模块如何分工：</strong>我会将学术空间理解为相对长期的研究资产集合，将 Research Labs 理解为围绕当前问题组织材料与产出的工作单元。分工明确后，用户既能跨任务复用资料，也不必在每次开始研究时面对整个知识库。设计重点应放在材料如何进入任务、产出如何回到积累中，避免形成两个孤立的存储入口。",
        "<strong>可信度影响使用深度：</strong>学术用户需要判断结论依据是否足够。AI 整合信息的便利，必须伴随核对原文与区分来源的便利；否则省下的阅读时间会变成额外的验证时间。我的产品判断是，生成速度之外，还应重视依据的可检查性，以及用户能否保留自己的判断和修改。",
        "<strong>增长与付费的承接：</strong>技术论文内容可以吸引有明确需求的人，但阅读量本身不会自动转化为长期使用。内容入口应承接到具体研究任务，让用户尽早体验资料积累带来的便利。商业化上，我更倾向先验证持续研究中哪些能力具有稳定价值，再设计权益边界；过早阻断基础阅读与积累，可能同时增加试用和留存的阻力。"
      ],
      metrics: [
        {
          "v": "22.1%",
          "l": "学术空间上线一周内使用率"
        },
        {
          "v": "31.4%",
          "l": "Research Labs 上线一周内使用率"
        },
        {
          "v": "17.9% → 21.4%",
          "l": "产品整体七日留存率"
        }
      ],
      visuals: [
        { img: A("aminer-1.png"), label: "AMiner 界面", caption: "AI 学术工具相关产品界面。" },
        { img: A("aminer-3.png"), label: "Research Labs", caption: "Research Labs 相关产品界面。" },
        { img: A("aminer-4.png"), label: "学术空间", caption: "学术空间界面展示。" },
      ],
    },
  ],

  projects: [
    {
      id: "exitem",
      title: "Exitem",
      en: "Zotero-Exitem",
      tag: "开源项目",
      cat: "AI 辅助文献提取与综述管理 · Zotero 插件",
      period: "个人项目 · 开源项目",
      color: "var(--orange)",
      cover: A("exitem-1.png"),
      images: [A("exitem-1.png"), A("exitem-2.png"), A("exitem-3.png"), A("exitem-4.png")],
      desc: "围绕 Zotero 的文献提取与综述管理插件，<strong>把 PDF 阅读、批注、提炼、综述写作串成一条连续动作</strong>。",
      pain: [
        "做文献综述时，要在 <strong>PDF 阅读、批注、高亮、笔记、表格与综述文档之间反复切换</strong>，现有工具把这条连续链路拆散了。",
        "高亮沉在 PDF 里、笔记散落各处，跨文献归纳与导出写作素材时还得手动重新整理一遍。",
      ],
      approach: [
        "借助 zotero-gpt 支持单条和批量（≤5）<strong>AI 提炼</strong>，把 PDF 高亮、批注和原文上下文自动并入提取字段。",
        "在 Zotero 内嵌标签页提供综述管理器，支持记录管理、文件夹合并合成、原始记录编辑与导出，让文献从阅读材料直接变成可复用的综述素材。",
      ],
      highlights: ["AI 单条/批量提炼", "综述管理器", "文件夹合成", "Zotero 8"],
      stack: ["Zotero 插件", "TypeScript"],
      metrics: [
        { v: "Zotero 8", l: "兼容版本" },
      ],
      evalStandard: [
        "<strong>内容准确性</strong>：提取内容与原文语义是否一致，无错位、无幻觉、无错漏。",
        "<strong>提取完整度</strong>：高亮、批注、标题、作者与摘要等字段是否完整并入，无缺失。",
        "<strong>信息提取效率</strong>：衡量单条信息点提取的 token 消耗，控制成本与延迟。",
        "<strong>素材可用性</strong>：导出到综述管理器的记录能否直接用于写作，分类与格式是否正确。",
      ],
      evalSystem: [
        "用覆盖不同学科、版式与篇幅的论文测试集跑单条/批量提炼，固定种子文献复测。",
        "人工对照原文逐字段核对，按「完全正确 / 部分正确 / 错误」三档打分，统计字段完整率与提取准确率。",
        "每次改动 Prompt、字段规则或批量逻辑后回归测试，用分数变化判断是否回归，沉淀失败样例作为复盘依据。",
      ],
    },
    {
      id: "miaobi",
      title: "妙笔",
      en: "Miaobi",
      tag: "黑客马拉松",
      cat: "自进化 Skills 引擎 · 京东客服提效与商家自运营",
      period: "黑客马拉松 · 2026/06 — 07",
      color: "var(--pink)",
      cover: A("miaobi-1.png"),
      images: [A("miaobi-1.png"), A("miaobi-2.png"), A("miaobi-3.png"), A("miaobi-4.png"), A("miaobi-5.png")],
      desc: "基于 AgentScope 的<strong>自进化 Skills 引擎</strong>：把商家的非标准资料沉淀为标准 SOP，再转化为可直接对接到线上环境的 Skills，<strong>从真实反馈中持续自进化</strong>。",
      pain: [
        "商家资料零散、非标准，无法直接用于客服问答，需<strong>人工整理成标准 SOP</strong>，链路长、成本高。",
        "Skills 上线后普遍<strong>缺少与线上环境的评测对接</strong>，效果好坏只能凭感觉判断。",
        "线上用户反馈与人工客服处理经验<strong>无法回流</strong>，Skills 因此难以持续变好。",
      ],
      approach: [
        "基于 AgentScope 独立开发「妙笔」，将商家<strong>非标准资料自动结构化、沉淀为标准 SOP</strong>，再转化为可对接到线上客服环境的 Skills。",
        "Skills 支持接入线上环境评测，并持续<strong>从线上用户反馈数据与人工客服数据中提炼经验</strong>、沉淀调试策略，让 Skills 在真实使用中不断自进化。",
      ],
      highlights: ["商家 Skills 自动生成", "系统层自进化", "评测对齐线上"],
      stack: ["AgentScope", "TypeScript"],
      metrics: [
        { v: "+23%", l: "客服平均提效" },
        { v: "60+", l: "接入商家数" },
      ],
    },
    {
      id: "laifuer",
      title: "来福er",
      en: "Laifuer",
      tag: "商业化尝试",
      cat: "AI 求职助手 · 网页应用",
      period: "个人商业化尝试 · 2026/03 — 至今",
      color: "var(--mint)",
      cover: A("laifuer-1.png"),
      images: [A("laifuer-1.png"), A("laifuer-2.png"), A("laifuer-3.png"), A("laifuer-4.png"), A("laifuer-5.png"), A("laifuer-6.png"), A("laifuer-7.png")],
      desc: "AI 求职助手：以 <strong>AI 对话为入口</strong>，支持投递管理、AI 简历、面试 mock，并能<strong>根据面试录音/文字记录自动复盘</strong>，把求职全程串在一起。",
      pain: [
        "投递记录散落在不同平台，难以统一跟踪与管理。",
        "简历反复修改却难有抓手，缺少针对性的反馈与优化方向。",
        "面试结束只剩零散记忆，复盘无从谈起。",
      ],
      approach: [
        "以 AI 对话为唯一入口，支持<strong>投递管理、AI 简历、面试 mock</strong>，并能<strong>根据面试录音/文字记录自动复盘</strong>。",
        "系统负责结构化、归档、摘要与自然语言检索；前端 React + Vite，后端 Python FastAPI + SQLite。",
        "靠朋友圈种子用户和社群口碑传播，保持零投放有机增长。",
      ],
      highlights: ["投递管理", "AI 简历", "面试 Mock", "录音/记录复盘"],
      stack: ["React", "Python FastAPI", "SQLite"],
      metrics: [
        { v: "1500+", l: "累计注册用户" },
        { v: "137", l: "日均活跃 DAU" },
        { v: "26.1%", l: "7 日留存" },
        { v: "6800+", l: "累计 AI 交互" },
      ],
    },
  ],

  skills: [
    {
      icon: "pen", title: "原型 & 文档", color: "var(--orange)",
      items: [
        { name: "墨刀", desc: "快速搭建可交互原型，验证移动端产品方案。" },
        { name: "Axure", desc: "高保真原型设计，承载复杂交互与页面流程。" },
      ],
    },
    {
      icon: "spark", title: "Agent 搭建", color: "var(--blue)",
      items: [
        { name: "AgentScope", desc: "多 Agent 编排与评测，支撑智能体工程化落地。" },
        { name: "LangChain", desc: "编排 LLM 应用与工具调用，串起检索生成链路。" },
        { name: "Dify", desc: "低代码可视化构建 AI 应用与工作流。" },
        { name: "Coze", desc: "智能体搭建平台，快速发布对话式应用。" },
        { name: "SpringAI", desc: "Java 生态 AI 集成，接入企业级后端。" },
      ],
    },
    {
      icon: "compass", title: "数据 & 编程", color: "var(--pink)",
      items: [
        { name: "Python", desc: "数据分析、后端与 AI 开发的主力语言。" },
        { name: "机器学习", desc: "监督/无监督建模，覆盖特征工程与评估。" },
        { name: "深度学习", desc: "神经网络与多模态表征学习。" },
        { name: "SQL", desc: "数据查询与清洗，支撑指标取数。" },
        { name: "R / SPSS / Mplus", desc: "统计建模与潜变量分析，服务实证研究。" },
      ],
    },
    {
      icon: "layers", title: "内容编辑", color: "var(--mint)",
      items: [
        { name: "数据可视化", desc: "用 Flourish 等工具把数据讲成清晰、可传播的故事。" },
        { name: "秀米", desc: "图文排版与新媒体推文设计，承载内容运营。" },
        { name: "剪映", desc: "短视频剪辑与字幕特效，快速产出传播素材。" },
        { name: "Canva", desc: "海报、封面与演示设计，统一视觉表达。" },
      ],
    },
  ],

  tools: ["Python", "AgentScope", "LangChain", "Dify", "Coze", "Axure", "墨刀", "Flourish", "秀米", "Canva", "剪映", "SQL"],

  contact: {
    email: "alansirius@163.com",
    socials: [
      { name: "alansirius@163.com", icon: "mail", href: "mailto:alansirius@163.com" },
      { name: "GitHub", icon: "link", href: "https://github.com/alansirius" },
    ],
  },
};

/* ---------- inline SVG icons (thick stroke) ---------- */
function Icon({ name, style }) {
  const p = { fill: "none", stroke: "currentColor", strokeWidth: 2.4, strokeLinecap: "round", strokeLinejoin: "round" };
  const paths = {
    compass: <g {...p}><circle cx="12" cy="12" r="9" /><path d="M15.5 8.5 13 13l-4.5 2.5L11 11z" /></g>,
    spark: <g {...p}><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18" /></g>,
    pen: <g {...p}><path d="M4 20l4-1L19 8a2.8 2.8 0 0 0-4-4L4 15l-1 4z" /><path d="M14 6l4 4" /></g>,
    mail: <g {...p}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></g>,
    link: <g {...p}><path d="M9 15l6-6" /><path d="M11 6l1-1a4 4 0 0 1 6 6l-1 1" /><path d="M13 18l-1 1a4 4 0 0 1-6-6l1-1" /></g>,
    doc: <g {...p}><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4M9 12h6M9 16h6" /></g>,
    cap: <g {...p}><path d="M12 4 2 9l10 5 10-5-10-5z" /><path d="M6 11v5c0 1 2.7 2.5 6 2.5s6-1.5 6-2.5v-5" /></g>,
    arrow: <g {...p}><path d="M5 12h14M13 6l6 6-6 6" /></g>,
    layers: <g {...p}><path d="M12 3 3 8l9 5 9-5-9-5z" /><path d="M3 13l9 5 9-5M3 18l9 5 9-5" /></g>,
  };
  return <svg viewBox="0 0 24 24" style={style} aria-hidden="true">{paths[name] || null}</svg>;
}

Object.assign(window, { PORTFOLIO, Icon });

/* ============================================================
   sections.jsx — presentational sections
   Exports to window: Nav, Hero, Marquee, Education,
   Experience, Work, ProjectModal, Skills, Contact, useReveal
   ============================================================ */

const { useState, useEffect, useRef } = React;

/* reveal-on-scroll hook */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal:not(.is-in)");
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("is-in"); io.unobserve(e.target); }
      });
    }, { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  });
}

/* ---------------- NAV ---------------- */
function Nav() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");
  const links = [
    ["#about", "关于我"],
    ["#education", "教育"],
    ["#experience", "实习"],
    ["#work", "项目"],
    ["#skills", "技能"],
    ["#contact", "联系我"],
  ];
  useEffect(() => {
    const ids = links.map(([href]) => href.slice(1));
    const spy = () => {
      const y = window.scrollY + 140;
      let cur = "";
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top + window.scrollY <= y) cur = id;
      }
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 40) cur = "contact";
      setActive(cur);
    };
    spy();
    window.addEventListener("scroll", spy, { passive: true });
    window.addEventListener("resize", spy, { passive: true });
    return () => {
      window.removeEventListener("scroll", spy);
      window.removeEventListener("resize", spy);
    };
  }, []);
  return (
    <nav className="nav">
      <div className="nav__brand">
        <span className="nav__dot"></span>
        {PORTFOLIO.name}
      </div>
      <div className={"nav__links" + (open ? " is-open" : "")}>
        {links.map(([href, label]) => (
          <a
            key={href}
            href={href}
            className={"nav__tab" + (active === href.slice(1) ? " is-active" : "")}
            onClick={() => setOpen(false)}
          >{label}</a>
        ))}
      </div>
      <button className="nav__burger" aria-label="菜单" onClick={() => setOpen((o) => !o)}>
        <span></span>
      </button>
    </nav>
  );
}

/* ---------------- HERO (draggable stickers) ---------------- */
function DraggableChip({ chip, boundsRef }) {
  const ref = useRef(null);
  const drag = useRef({ active: false, dx: 0, dy: 0 });

  const onDown = (e) => {
    const el = ref.current;
    const pt = e.touches ? e.touches[0] : e;
    const rect = el.getBoundingClientRect();
    drag.current = { active: true, dx: pt.clientX - rect.left, dy: pt.clientY - rect.top };
    el.style.zIndex = 20;
  };

  useEffect(() => {
    const move = (e) => {
      if (!drag.current.active) return;
      const pt = e.touches ? e.touches[0] : e;
      const parent = boundsRef.current.getBoundingClientRect();
      let x = pt.clientX - parent.left - drag.current.dx;
      let y = pt.clientY - parent.top - drag.current.dy;
      const el = ref.current;
      x = Math.max(-20, Math.min(x, parent.width - el.offsetWidth + 20));
      y = Math.max(-20, Math.min(y, parent.height - el.offsetHeight + 20));
      el.style.left = x + "px";
      el.style.top = y + "px";
      el.style.transform = "rotate(0deg)";
      if (e.cancelable) e.preventDefault();
    };
    const up = () => { drag.current.active = false; };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [boundsRef]);

  return (
    <div
      ref={ref}
      className="chip"
      onMouseDown={onDown}
      onTouchStart={onDown}
      style={{ left: chip.x + "%", top: chip.y + "%", transform: `rotate(${chip.rot}deg)` }}
    >
      <span className="emo" style={{ background: chip.color }}></span>
      {chip.text}
    </div>
  );
}

function Hero() {
  const stageRef = useRef(null);
  const h = PORTFOLIO.hero;
  return (
    <header className="hero wrap" id="about">
      <div className="hero__grid">
        <div className="hero__intro">
          <span className="eyebrow">{h.kicker}</span>
          <h1 className="hero__title">
            你好，<br />
            <span className="hero__name">我是 <span className="hl">{PORTFOLIO.name}</span>。</span>
          </h1>
          <p className="hero__tagline">{PORTFOLIO.aboutLead}</p>
          <p className="hero__lead" dangerouslySetInnerHTML={{ __html: h.statement }} />
          <div className="hero__cta">
            <a href="#work" className="btn">看我的项目 <Icon name="arrow" style={{ width: 18, height: 18 }} /></a>
            <a href={PORTFOLIO.hero.primaryHref || "https://github.com/alansirius"} className="btn btn--ghost">GitHub</a>
          </div>
        </div>

        <div className="hero__stage" ref={stageRef}>
          <img className="hero__avatar" src={PORTFOLIO.avatar} alt={PORTFOLIO.name} />
          {h.stickers.map((c, i) => (
            <DraggableChip key={i} chip={c} boundsRef={stageRef} />
          ))}
          <span className="chip__hint">↑ 拖拖看这些贴纸 ✦</span>
        </div>
      </div>

      <div className="hero__stats stat-grid">
        {PORTFOLIO.stats.map((s, i) => (
          <div key={i} className="sticker stat" style={{ transitionDelay: i * 80 + "ms" }}>
            <div className="stat__num" style={{ color: s.color }}>{s.num}</div>
            <div className="stat__label">{s.label}</div>
          </div>
        ))}
      </div>
    </header>
  );
}

/* ---------------- MARQUEE ---------------- */
function Marquee() {
  const run = (
    <span>
      {PORTFOLIO.marquee.map((w, i) => (
        <React.Fragment key={i}>{w} <span className="marquee__star">✦</span> </React.Fragment>
      ))}
    </span>
  );
  return (
    <div className="marquee" aria-hidden="true">
      <div className="marquee__track">{run}{run}</div>
    </div>
  );
}

/* ---------------- EDUCATION ---------------- */
function Education() {
  return (
    <section className="section wrap" id="education">
      <div className="section__head reveal">
        <div>
          <span className="section__num">// 02</span>
          <h2 className="section__title">教育背景</h2>
        </div>
      </div>
      <div className="edu__grid">
        {PORTFOLIO.education.map((e, i) => (
          <article key={i} className="sticker edu-card reveal" style={{ transitionDelay: i * 80 + "ms" }}>
            <div className="edu-card__media">
              <img src={e.img} alt={e.school} />
              <span className="edu-card__badge" style={{ background: e.color }}>
                <Icon name="cap" style={{ width: 18, height: 18, color: "#fff" }} />
              </span>
            </div>
            <div className="edu-card__body">
              <div className="edu-card__period">{e.period}</div>
              <h3 className="edu-card__school">{e.school}</h3>
              <div className="edu-card__degree" style={{ color: e.color }}>{e.degree}</div>
              <p className="edu-card__detail" dangerouslySetInnerHTML={{ __html: e.detail }} />
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

/* ---------------- EXPERIENCE (tabs) ---------------- */
function ExpStack({ visuals, color, onImage, title }) {
  const [i, setI] = useState(0);
  const n = visuals.length;
  const go = (d) => setI((p) => (p + d + n) % n);
  const cur = visuals[i];
  const cardStyle = (rel) => {
    if (rel === 0) return { transform: "translate(0,0) rotate(0deg) scale(1)", zIndex: 30, opacity: 1 };
    if (rel === 1) return { transform: "translate(20px,15px) rotate(3.5deg) scale(.955)", zIndex: 20, opacity: 1 };
    if (rel === 2) return { transform: "translate(36px,28px) rotate(6.5deg) scale(.915)", zIndex: 10, opacity: 1 };
    return { transform: "translate(48px,38px) rotate(9deg) scale(.9)", zIndex: 0, opacity: 0, pointerEvents: "none" };
  };
  return (
    <div className="exp__stack">
      {title && <div className="exp__stack-title" style={{ color }}>{title}</div>}
      <div className="exp__deck">
        {visuals.map((v, idx) => {
          const rel = (idx - i + n) % n;
          return (
            <figure
              key={idx}
              className="exp__card"
              style={{ ...cardStyle(rel), borderColor: rel === 0 ? "var(--ink)" : "var(--ink)" }}
              onClick={rel === 0 ? () => onImage(v.img, v.caption) : () => setI(idx)}
            >
              <span className="exp__card-bar" style={{ background: color }}></span>
              <img src={v.img} alt={v.label} />
            </figure>
          );
        })}
      </div>
      <div className="exp__stack-foot">
        {n > 1 && (
          <button className="exp__nav" onClick={() => go(-1)} aria-label="上一张" style={{ borderColor: color }}>
            <Icon name="arrow" style={{ width: 18, height: 18, transform: "rotate(180deg)", color }} />
          </button>
        )}
        <div className="exp__cap">
          <span className="exp__cap-label">{cur.label}</span>
          {n > 1 && (
            <span className="exp__cap-count" style={{ color }}>
              {String(i + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}
            </span>
          )}
        </div>
        {n > 1 && (
          <button className="exp__nav" onClick={() => go(1)} aria-label="下一张" style={{ borderColor: color }}>
            <Icon name="arrow" style={{ width: 18, height: 18, color }} />
          </button>
        )}
      </div>
    </div>
  );
}

function Experience({ onImage }) {
  const [active, setActive] = useState(0);
  const exp = PORTFOLIO.experience[active];
  return (
    <section className="section wrap" id="experience">
      <div className="section__head reveal">
        <div>
          <span className="section__num">// 03</span>
          <h2 className="section__title">实习经历</h2>
        </div>
      </div>

      <div className="exp reveal">
        <div className="exp__tabs" role="tablist">
          {PORTFOLIO.experience.map((e, i) => (
            <button
              key={e.id}
              role="tab"
              className={"exp__tab" + (i === active ? " is-active" : "")}
              onClick={() => setActive(i)}
              style={i === active ? { "--tab": e.color } : undefined}
            >
              <span className="exp__tab-dot" style={{ background: e.color }}></span>
              <span className="exp__tab-co">{e.short}</span>
              <span className="exp__tab-pd">{e.period}{e.current ? " · 在职" : ""}</span>
            </button>
          ))}
        </div>

        <div className="exp__panel sticker" key={exp.id}>
          <div className="exp__panel-head">
            <div>
              <h3 className="exp__role">{exp.role}<span className="exp__at"> @ {exp.company} · {exp.team}</span></h3>
              <div className="exp__period">{exp.period}{exp.current && <span className="exp__live">● 进行中</span>}</div>
            </div>
          </div>
          <div className={"exp__intro" + (exp.visuals ? " exp__intro--media" : "")}>
            <div className="exp__intro-text">
              <p className="exp__summary" dangerouslySetInnerHTML={{ __html: exp.summary }} />
              <div className="exp__metrics">
                {exp.metrics.map((m, i) => (
                  <div key={i} className="exp__metric">
                    <b style={{ color: exp.color }}>{m.v}</b>
                    <span>{m.l}</span>
                  </div>
                ))}
              </div>
            </div>
            {exp.visuals && <ExpStack visuals={exp.visuals} color={exp.color} onImage={onImage} />}
          </div>

          {exp.visualGroups && (
            <div className="exp__visual-groups">
              {exp.visualGroups.map((group) => (
                <ExpStack
                  key={group.title}
                  title={group.title}
                  visuals={group.visuals}
                  color={exp.color}
                  onImage={onImage}
                />
              ))}
            </div>
          )}

          <div className="exp__work">
            {exp.work.map((w, i) => (
              <div key={i} className="exp__work-item">
                <span className="exp__work-tag" style={{ borderColor: exp.color, color: exp.color }}>{w.label}</span>
                <p dangerouslySetInnerHTML={{ __html: w.body }} />
              </div>
            ))}
          </div>

          <div className="exp__think" style={{ borderColor: exp.color }}>
            <span className="exp__think-mark" style={{ background: exp.color }}>我的思考</span>
            <ul className="exp__think-list">
              {exp.thinking.map((item, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- WORK (projects) ---------------- */
function Work({ onOpen }) {
  const list = PORTFOLIO.projects;
  return (
    <section className="section wrap" id="work">
      <div className="section__head">
        <div className="reveal">
          <span className="section__num">// 04</span>
          <h2 className="section__title">个人项目</h2>
        </div>
      </div>
      <div className="work__grid">
        {list.map((p) => (
          <article key={p.id} className="sticker card reveal" onClick={() => onOpen(p)}>
            <div className="card__media">
              <img src={p.cover} alt={p.title} />
              <span className="card__tag">{p.tag}</span>
              {p.images.length > 1 && <span className="card__count">{p.images.length} 图</span>}
            </div>
            <div className="card__body">
              <h3 className="card__title">{p.title} <span className="card__en">{p.en}</span></h3>
              <div className="card__role">{p.cat}</div>
              <p className="card__desc" dangerouslySetInnerHTML={{ __html: p.desc }} />
              <div className="card__metrics">
                {p.metrics.slice(0, 3).map((m, i) => (
                  <div key={i} className="card__metric">
                    <b style={{ color: p.color }}>{m.v}</b>
                    <span>{m.l}</span>
                  </div>
                ))}
              </div>
              <div className="card__open" style={{ marginTop: 18 }}>
                <span className="btn btn--ghost" style={{ padding: "10px 18px", fontSize: ".9rem", pointerEvents: "none" }}>
                  查看案例 <Icon name="arrow" style={{ width: 16, height: 16 }} />
                </span>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

/* ---------------- PROJECT MODAL ---------------- */
function Bullets({ items }) {
  return Array.isArray(items)
    ? <ul>{items.map((x, i) => <li key={i} dangerouslySetInnerHTML={{ __html: x }} />)}</ul>
    : <p dangerouslySetInnerHTML={{ __html: items }} />;
}

function ProjectModal({ project, onClose, onImage }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);
  if (!project) return null;
  const p = project;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal__close" onClick={onClose} aria-label="关闭">×</button>
        <div className="modal__inner">
        <div className="modal__media">
          <div className="modal__stage">
            {p.images.length > 1 && (
              <button
                className="modal__arrow modal__arrow--prev"
                onClick={() => setIdx((idx - 1 + p.images.length) % p.images.length)}
                aria-label="上一张"
              >
                <Icon name="arrow" style={{ width: 22, height: 22, transform: "rotate(180deg)" }} />
              </button>
            )}
            <img src={p.images[idx]} alt={p.title} onClick={() => onImage(p.images[idx])} />
            {p.images.length > 1 && (
              <button
                className="modal__arrow modal__arrow--next"
                onClick={() => setIdx((idx + 1) % p.images.length)}
                aria-label="下一张"
              >
                <Icon name="arrow" style={{ width: 22, height: 22 }} />
              </button>
            )}
          </div>
          {p.images.length > 1 && (
            <div className="modal__thumbs">
              {p.images.map((im, i) => (
                <button
                  key={i}
                  className={"modal__thumb" + (i === idx ? " is-active" : "")}
                  onClick={() => setIdx(i)}
                  style={{ backgroundImage: `url(${im})` }}
                  aria-label={"图 " + (i + 1)}
                ></button>
              ))}
            </div>
          )}
        </div>
        <div className="modal__body">
          <span className="card__tag" style={{ position: "static", display: "inline-block", marginBottom: 12 }}>{p.tag}</span>
          <h3 className="modal__title">{p.title} <span className="card__en">{p.en}</span></h3>
          <div className="modal__role">{p.cat} · {p.period}</div>

          <div className="modal__metrics">
            {p.metrics.map((m, i) => (
              <div key={i} className="sticker stat">
                <div className="stat__num" style={{ color: p.color, fontSize: "1.7rem" }}>{m.v}</div>
                <div className="stat__label">{m.l}</div>
              </div>
            ))}
          </div>

          <div className="modal__section">
            <h4>问题</h4>
            <Bullets items={p.pain} />
          </div>
          <div className="modal__section">
            <h4>我的解法</h4>
            <Bullets items={p.approach} />
          </div>
          <div className="modal__section">
            <h4>亮点</h4>
            <div className="chip-row">
              {p.highlights.map((h) => <span key={h} className="mini-chip" style={{ background: p.color }}>{h}</span>)}
            </div>
          </div>
          <div className="modal__section">
            <h4>技术栈</h4>
            <div className="chip-row">
              {p.stack.map((s) => <span key={s} className="mini-chip mini-chip--ghost">{s}</span>)}
            </div>
          </div>
          {p.evalStandard && (
            <div className="modal__section">
              <h4>评测标准</h4>
              <Bullets items={p.evalStandard} />
            </div>
          )}
          {p.evalSystem && (
            <div className="modal__section">
              <h4>评测体系</h4>
              <Bullets items={p.evalSystem} />
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- LIGHTBOX ---------------- */
function Lightbox({ src, caption, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!src) return null;
  return (
    <div className="lightbox" onClick={onClose}>
      <button className="modal__close lightbox__close" onClick={onClose} aria-label="关闭">×</button>
      <figure onClick={(e) => e.stopPropagation()}>
        <img src={src} alt={caption || ""} />
        {caption && <figcaption>{caption}</figcaption>}
      </figure>
    </div>
  );
}

/* ---------------- SKILLS ---------------- */
function Skills() {
  return (
    <section className="section wrap" id="skills">
      <div className="section__head reveal">
        <div>
          <span className="section__num">// 05</span>
          <h2 className="section__title">技能栈</h2>
        </div>
      </div>
      <div className="skills__grid">
        {PORTFOLIO.skills.map((s, i) => (
          <div key={i} className="sticker skill-card reveal" style={{ transitionDelay: i * 80 + "ms" }}>
            <div className="skill-card__icon" style={{ background: s.color, color: "#fff" }}>
              <Icon name={s.icon} />
            </div>
            <h3>{s.title}</h3>
            <ul className="skill-list">
              {s.items.map((it, j) => (
                <li key={j}>
                  <b style={{ color: s.color }}>{it.name}</b>
                  <span>{it.desc}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- CONTACT ---------------- */
function Contact() {
  return (
    <section className="section wrap contact" id="contact">
      <div className="contact__card reveal">
        <span className="eyebrow" style={{ background: "var(--yellow)" }}>// 06 — 保持联系</span>
        <h2 className="contact__title">有好点子？<br />一起把它做出来。</h2>
        <p className="contact__lead">
          无论是 AI 产品合作、实习/校招机会，还是单纯想聊聊 Agent，我都很乐意收到你的消息。
        </p>
        <div className="contact__links">
          {PORTFOLIO.contact.socials.map((s) => (
            <a key={s.name} className="social" href={s.href} target={s.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">
              <Icon name={s.icon} /> {s.name}
            </a>
          ))}
        </div>
      </div>
      <div className="footer">
        © 2026 {PORTFOLIO.name} · {PORTFOLIO.handle} — 用 ✦ 与好奇心搭建
      </div>
    </section>
  );
}

Object.assign(window, {
  Nav, Hero, Marquee, Education, Experience, Work, ProjectModal, Lightbox, Skills, Contact, useReveal,
});


    const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
      "accent": "#FF6B35",
      "fontScale": 100,
      "texture": "dots"
    }/*EDITMODE-END*/;

    function App() {
      const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
      const [active, setActive] = useState(null);
      const [lightbox, setLightbox] = useState(null);
      const openImage = (src, caption) => setLightbox({ src, caption });
      useReveal();

      // apply tweaks to :root
      useEffect(() => {
        const r = document.documentElement;
        r.style.setProperty("--accent", t.accent);
        document.body.style.fontSize = (t.fontScale / 100) + "rem";
      }, [t.accent, t.fontScale]);

      useEffect(() => {
        const b = document.body;
        if (t.texture === "none") { b.style.backgroundImage = "none"; }
        else if (t.texture === "grid") {
          b.style.backgroundImage =
            "linear-gradient(var(--paper-2) 1px, transparent 1px), linear-gradient(90deg, var(--paper-2) 1px, transparent 1px)";
          b.style.backgroundSize = "26px 26px";
        } else {
          b.style.backgroundImage = "radial-gradient(var(--paper-2) 1.4px, transparent 1.4px)";
          b.style.backgroundSize = "22px 22px";
        }
      }, [t.texture]);

      return (
        <React.Fragment>
          <Nav />
          <Hero />
          <Marquee />
          <Education />
          <Experience onImage={openImage} />
          <Work onOpen={setActive} />
          <Skills />
          <Contact />
          {active && <ProjectModal project={active} onClose={() => setActive(null)} onImage={openImage} />}
          {lightbox && <Lightbox src={lightbox.src} caption={lightbox.caption} onClose={() => setLightbox(null)} />}

          <TweaksPanel>
            <TweakSection label="主题色" />
            <TweakColor label="强调色" value={t.accent}
              options={["#FF6B35", "#2D5BFF", "#14C6A4", "#FF4D9D"]}
              onChange={(v) => setTweak("accent", v)} />
            <TweakSection label="排版" />
            <TweakSlider label="字号" value={t.fontScale} min={90} max={115} unit="%"
              onChange={(v) => setTweak("fontScale", v)} />
            <TweakSection label="背景纹理" />
            <TweakRadio label="纹理" value={t.texture}
              options={["dots", "grid", "none"]}
              onChange={(v) => setTweak("texture", v)} />
          </TweaksPanel>
        </React.Fragment>
      );
    }

    ReactDOM.createRoot(document.getElementById("root")).render(<App />);

    // announce upgradeable
    function announceUpgrade() {
      window.parent.postMessage({ type: "miaoda:upgrade:available", kind: "interactive-prototype" }, "*");
    }
    announceUpgrade();
    if (document.readyState !== "complete") window.addEventListener("load", announceUpgrade, { once: true });
  
